<template>
  <div style="height: 100%; position: relative;">
    <draggable
      id="pageRowsList"
      :list="pageRowsState"
      :disabled="!enabled"
      style="height: 100%;"
      class="block grid ml-4 mr-4"
      handle=".handle"
      v-bind="dragOptions"
      :data-stats-builder="true"
      @start="dragging = true"
      @end="dragging = false"
    >
      <template #item="{ element }">
        <div v-if="element.type === 'placeholder'" class="m-4 placeholder-on-add-row" />
        <div
          v-else
          class="root-row m-4"
          :class="{ 'not-draggable': !enabled }"
        >
          <StatsBuilderRow
            :row-data="element"
          />
        </div>
      </template>
    </draggable>
    <div
      v-if="isEmpty"
      class="stats-builder-empty-state"
    >
      <div class="stats-builder-empty-state__content">
        <div class="stats-builder-empty-state__icon">
          <i class="fa-regular fa-chart-mixed" />
        </div>
        <h2 class="stats-builder-empty-state__title">{{ t('statsEditor.emptyState.title') }}</h2>
        <p class="stats-builder-empty-state__description">{{ t('statsEditor.emptyState.description') }}</p>
        <button
          class="stats-builder-empty-state__button"
          @click="openElementsPanel"
        >
          <i class="fa-regular fa-plus" />
          <span>{{ t('statsEditor.emptyState.button') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  ref,
} from 'vue';
import draggable from 'vuedraggable';
import StatsBuilderRow from '@/components/stats-builder/StatsBuilderRow.vue';
import { StatsBuilderRowConfig } from '@/types/stats-editor-types';
import { StatsEditorPanelEnum } from '@/types';
import Tooltip from 'primevue/tooltip';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';

export default defineComponent({
  name: 'StatsBuilder',

  components: {
    StatsBuilderRow,
    draggable,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    pageRowsState: {
      type: Array as PropType<StatsBuilderRowConfig[]>,
      required: true,
    },
  },

  setup(props: { pageRowsState: StatsBuilderRowConfig[] }) {
    const { t } = useI18n();
    const store = useStore();
    const enabled = ref(true);
    const dragging = ref(false);
    const dragOptions = ref({
      animation: 200,
      group: { name: 'line_builder', pull: false, put: ['line_element', 'widget_element'] },
      disabled: false,
      onStart: () => {
        store.commit('statsEditor/setIsDraggingBuilderItem', true);
      },
      onEnd: () => {
        store.commit('statsEditor/setIsDraggingBuilderItem', false);
      },
    });

    const isEmpty = computed(() => props.pageRowsState.length === 0);

    const openElementsPanel = () => {
      store.commit('statsEditor/showLeftToolbar', StatsEditorPanelEnum.ELEMENTS_LIST_PANEL);
    };

    return {
      t,
      enabled,
      dragging,
      dragOptions,
      isEmpty,
      openElementsPanel,
    };
  },
});
</script>

<style lang="scss">
#pageRowsList {
  .root-row {
    position: relative;
  }

  .placeholder-on-add-row {
    position: relative;
    height: 0;
    border-bottom: 2px dashed rgba(148, 200, 64, 0.45);

    &::before {
      content: 'Insérer ici';
      position: absolute;
      top: -0.85rem;
      left: 50%;
      transform: translate(-50%, -100%);
      padding: 0.4rem 0.8rem 0.35rem;
      border-radius: 999px;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(231, 247, 206, 0.92));
      border: 1px solid rgba(148, 200, 64, 0.45);
      color: #2f4310;
      font-size: 0.75rem !important;
      font-weight: 650;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      line-height: 1rem;
      box-shadow: 0 12px 24px -18px rgba(47, 71, 11, 0.4);
    }
  }

  .placeholder-on-add-widget {
    position: relative;
    height: 0;
    border-bottom: 2px dashed transparent;
    margin-bottom: 0.5rem;

    &::before {
      content: 'Insérer ici';
      position: absolute;
      top: -0.85rem;
      left: 0;
      padding: 0.35rem 0.75rem 0.3rem;
      border-radius: 999px;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(231, 247, 206, 0.92));
      border: 1px solid rgba(148, 200, 64, 0.45);
      color: #2f4310;
      font-size: 0.72rem;
      font-weight: 650;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      line-height: 1rem;
      box-shadow: 0 12px 24px -18px rgba(47, 71, 11, 0.35);
      opacity: 0;
      transform: translateY(6px);
      transition: opacity 0.2s ease, transform 0.2s ease;
      pointer-events: none;
      z-index: 1;
    }

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -2px;
      height: 4px;
      border-radius: 999px;
      background: linear-gradient(90deg, rgba(148, 200, 64, 0.35), rgba(184, 227, 106, 0.85));
      opacity: 0;
      transform: scaleX(0.6);
      transform-origin: left center;
      transition: opacity 0.25s ease, transform 0.25s ease;
      pointer-events: none;
    }

    &--allowed {
      border-bottom: 2px dashed rgba(148, 200, 64, 0.45);

      &::before {
        opacity: 1;
        transform: translateY(0);
      }

      &::after {
        opacity: 1;
        transform: scaleX(1);
      }
    }

    &--disallowed {
      border-bottom: 2px dashed rgba(200, 64, 64, 0.45);

      &::before {
        content: 'Non compatible';
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(254, 245, 245, 0.92));
        border-color: rgba(200, 64, 64, 0.45);
        color: #7a1f1f;
        opacity: 1;
        transform: translateY(0);
        box-shadow: 0 12px 24px -18px rgba(120, 28, 28, 0.35);
      }

      &::after {
        background: linear-gradient(90deg, rgba(200, 64, 64, 0.35), rgba(200, 64, 64, 0.6));
        opacity: 1;
        transform: scaleX(1);
      }
    }
  }
}

.p-overlaypanel-content {
  padding: 0 !important;
}
</style>

<style scoped lang="scss">
  .ghost {
    opacity: 0.5;
    background: #c8ebfb;
  }

  .stats-builder-empty-state {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;

    &__content {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      max-width: 400px;
      padding: 2rem;
      pointer-events: auto;
    }

    &__icon {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.5rem;
      font-size: 1.75rem;
      background: rgba(139, 195, 74, 0.1);
      color: #8bc34a;
    }

    &__title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0 0 0.5rem 0;
    }

    &__description {
      font-size: 0.875rem;
      color: #6b7280;
      margin: 0 0 1.5rem 0;
      line-height: 1.5;
    }

    &__button {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      background: #8bc34a;
      color: #fff;
      border: none;
      border-radius: 0.375rem;
      font-size: 0.9375rem;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s ease, transform 0.1s ease;

      &:hover {
        background: #7cb342;
      }

      &:active {
        transform: scale(0.98);
      }

      i {
        font-size: 0.875rem;
      }
    }
  }
</style>
